import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { PostCard, StateBlock } from "../components/Cards";
import { useAuth } from "../lib/auth";
import { createSectionPost, getSection, getSectionPosts, getUser } from "../lib/data";
import { setSeo } from "../lib/seo";
import { useAsync } from "../lib/useAsync";
import { isAppSection, type ForumUser } from "../lib/types";

export function SectionPage() {
  const { sectionId = "" } = useParams();
  const { user, login } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const { data: section, loading: sectionLoading, error: sectionError } = useAsync(() => getSection(sectionId), [sectionId]);
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const hasApp = isAppSection(section);
  const { data: posts, loading: postsLoading, error: postsError } = useAsync(
    () => (section ? getSectionPosts(sectionId) : Promise.resolve([])),
    [sectionId, section?.id, hasApp, refreshKey]
  );

  useEffect(() => {
    setSeo({
      title: `${section?.name || "Section"} | My Local Forum`,
      description: section?.description || "Browse this local forum section.",
      path: `/sections/${sectionId}`
    });
  }, [sectionId, section?.name, section?.description]);

  if (sectionLoading || sectionError || !section) {
    return (
      <section className="page">
        <h1>Section</h1>
        <StateBlock loading={sectionLoading} error={sectionError} empty={!sectionLoading && !section} />
      </section>
    );
  }

  if (hasApp) {
    return (
      <section className="section-app-page">
        <iframe
          className="section-app-frame"
          title={section.name}
          srcDoc={section.appHtml}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-downloads allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts"
        />
        <div className="section-app-posts">
          <h1>{section.name}</h1>
          {section.description ? <p className="section-description">{section.description}</p> : null}
          <SectionPostComposer
            sectionId={section.id}
            user={user}
            profile={profile}
            open={composerOpen}
            notice={notice}
            onLogin={login}
            onToggle={() => setComposerOpen((value) => !value)}
            onNotice={setNotice}
            onCreated={() => {
              setComposerOpen(false);
              setRefreshKey((key) => key + 1);
            }}
          />
          <StateBlock loading={postsLoading} error={postsError} empty={posts?.length === 0} />
          <div className="masonry">{posts?.map((post) => <PostCard key={post.id} post={post} />)}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="page">
      <h1>{section.name}</h1>
      {section.description ? <p className="section-description">{section.description}</p> : null}
      <SectionPostComposer
        sectionId={section.id}
        user={user}
        profile={profile}
        open={composerOpen}
        notice={notice}
        onLogin={login}
        onToggle={() => setComposerOpen((value) => !value)}
        onNotice={setNotice}
        onCreated={() => {
          setComposerOpen(false);
          setRefreshKey((key) => key + 1);
        }}
      />
      <StateBlock loading={postsLoading} error={postsError} empty={posts?.length === 0} />
      <div className="masonry">{posts?.map((post) => <PostCard key={post.id} post={post} />)}</div>
    </section>
  );
}

function SectionPostComposer({
  sectionId,
  user,
  profile,
  open,
  notice,
  onLogin,
  onToggle,
  onNotice,
  onCreated
}: {
  sectionId: string;
  user: unknown;
  profile?: ForumUser | null;
  open: boolean;
  notice: string | null;
  onLogin: () => Promise<void>;
  onToggle: () => void;
  onNotice: (notice: string | null) => void;
  onCreated: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!profile || submitting) return;

    const form = new FormData(event.currentTarget);
    const title = String(form.get("title") || "").trim();
    const content = String(form.get("content") || "").trim();
    const imageUrl = String(form.get("imageUrl") || "").trim() || null;
    if (!title || !content) {
      onNotice("Title and content are required.");
      return;
    }

    setSubmitting(true);
    onNotice(null);
    try {
      const result = await createSectionPost({ sectionId, user: profile, title, content, imageUrl });
      event.currentTarget.reset();
      onNotice(result.needsReview ? "Submitted for review." : "Post created. +5 coins");
      onCreated();
    } catch (err) {
      onNotice(err instanceof Error ? err.message : "Unable to create post.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="section-composer">
      <div className="section-composer-actions">
        {user ? (
          <button className="primary-button" type="button" onClick={onToggle}>
            {open ? "Cancel" : "Add New Post"}
          </button>
        ) : (
          <button className="primary-button" type="button" onClick={onLogin}>
            Sign in to Add New Post
          </button>
        )}
        <Link className="ghost-button" to="/feed">
          View Feed
        </Link>
      </div>
      {notice ? <div className="panel store-notice">{notice}</div> : null}
      {open && user ? (
        <form className="panel form-grid section-post-form" onSubmit={submit}>
          <input name="title" placeholder="Post title" required />
          <textarea name="content" placeholder="Write your post" required />
          <input name="imageUrl" placeholder="Image URL" />
          <button className="primary-button" type="submit" disabled={submitting || !profile}>
            {submitting ? "Posting..." : "Post"}
          </button>
        </form>
      ) : null}
    </div>
  );
}
